/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppConfig } from './app-config.model';
import { AppConfigLoaded, LoadAppConfig } from './app-config.actions';
import { RestService } from '../../../modules/shared/services/rest.service';
import { tap } from 'rxjs/operators';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { Injectable } from '@angular/core';

@State<AppConfig>({
    name: 'appConfig',
    defaults: {
        currentMap: '',
        loaded: false,
        map: {},
        defaultMap: '',
        enableLocation: true,
        print: {
            paper: [],
        },
        printDelay: 1500,
        tableLimits: {
            values: [10, 25, 50, 100],
            includeAll: true,
        },
        selectTolerance: 6,
        isolation: {},
        legendIcons: {
            width: 32,
            height: 24,
        },
        searches: {
            maxFeatures: 50,
            styles: [],
        },
        siteName: '',
        authenticators: [],
        autologin: {
            enabled: true,
            guestuser: '',
            guestpassword: '',
        },
        enableDisclaimer: true,
        enableRemember: false,
        mapTips: [],
        mapTipVariance: 3,
        mapTipsInitiallyEnabled: true,
        gspt: [],
        bufferRanges: [100, 200, 300, 400],
        liveResults: {
            template: [],
            key: '',
            layer: '',
        },
        siteDescription: '',
        zoomgoto: {
            layers: {},
            pointminwidth: 30,
            polygonzoommultiplier: 1.1,
        },
        menuBarImage: null,
        logClientMessages: false,
    },
})
@Injectable()
export class AppConfigState implements NgxsOnInit {
    constructor(private logger: LoggerService, private rest: RestService, private store: Store) {}

    ngxsOnInit() {
        this.store.dispatch(new LoadAppConfig());
    }

    @Action(LoadAppConfig)
    loadAppConfig({ getState, setState, dispatch }: StateContext<AppConfig>) {
        const state: AppConfig = DashGisUtil.clone(getState());
        return this.rest.apiGet('config').pipe(
            tap((config: AppConfig) => {
                config.loaded = true;
                const newState: AppConfig = { ...state, ...config };
                if (!state.currentMap) {
                    newState.currentMap = config.defaultMap;
                }
                setState(newState);
            }),
            tap(() => setTimeout(() => dispatch(new AppConfigLoaded()), 0))
        );
    }

    @Action(AppConfigLoaded)
    appConfigLoaded() {}

    @Selector()
    static loaded(state: AppConfig) {
        return state.loaded;
    }

    @Selector()
    static currentMap(state: AppConfig) {
        return state.currentMap;
    }

    @Selector()
    static defaultMap(state: AppConfig) {
        return state.defaultMap;
    }

    @Selector()
    static map(state: AppConfig) {
        return AppConfigState.getCurrentMap(state);
    }

    @Selector()
    static baseLayer(state: AppConfig) {
        return AppConfigState.getCurrentMap(state).baseLayer;
    }

    @Selector()
    static enableLocation(state: AppConfig) {
        return state.enableLocation;
    }

    @Selector()
    static print(state: AppConfig) {
        return state.print;
    }

    @Selector()
    static tableLimits(state: AppConfig) {
        return state.tableLimits;
    }

    @Selector()
    static selectTolerance(state: AppConfig) {
        return state.selectTolerance;
    }

    @Selector()
    static isolation(state: AppConfig) {
        return state.isolation;
    }

    @Selector()
    static legendIcons(state: AppConfig) {
        return state.legendIcons;
    }

    @Selector()
    static searches(state: AppConfig) {
        return state.searches;
    }

    @Selector()
    static siteName(state: AppConfig) {
        return state.siteName;
    }

    @Selector()
    static authenticators(state: AppConfig) {
        return state.authenticators;
    }

    @Selector()
    static autoLogin(state: AppConfig) {
        return state.autologin;
    }

    @Selector()
    static enableDisclaimer(state: AppConfig) {
        return state.enableDisclaimer;
    }

    @Selector()
    static enableRemember(state: AppConfig) {
        return state.enableRemember;
    }

    @Selector()
    static mapTips(state: AppConfig) {
        return state.mapTips;
    }

    @Selector()
    static mapTipVariance(state: AppConfig) {
        return state.mapTipVariance;
    }

    @Selector()
    static mapTipsInitiallyEnabled(state: AppConfig) {
        return state.mapTipsInitiallyEnabled;
    }

    @Selector()
    static gspt(state: AppConfig) {
        return state.gspt;
    }

    @Selector()
    static bufferRanges(state: AppConfig) {
        return state.bufferRanges;
    }

    @Selector()
    static liveResults(state: AppConfig) {
        return state.liveResults;
    }

    @Selector()
    static siteDescription(state: AppConfig) {
        return state.siteDescription;
    }

    @Selector()
    static zoomGoto(state: AppConfig) {
        return state.zoomgoto;
    }

    @Selector()
    static pageSizeOptions(state: AppConfig) {
        return state.tableLimits;
    }

    @Selector()
    static menuBarImage(state: AppConfig) {
        return state.menuBarImage;
    }

    @Selector()
    static printDelay(state: AppConfig) {
        return state.printDelay;
    }

    @Selector()
    static logClientMessages(state: AppConfig) {
        return state.logClientMessages;
    }

    private static getCurrentMap(state: AppConfig) {
        const currentMap = state.currentMap || state.defaultMap;
        return state.map[currentMap];
    }
}
